// src/pages/Private/Student/QuizView/TakeQuiz.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../../../utils/axios';
import './TakeQuiz.css';

function TakeQuiz() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    startQuiz();
  }, [quizId]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeLeft]);

  const startQuiz = async () => {
    try {
      const response = await axios.post(`/quizzes/${quizId}/start`);
      setAttempt(response.data.attemptId);
      setQuiz(response.data.quiz);
      setTimeLeft(response.data.quiz.timeLimit * 60);
      setAnswers(new Array(response.data.quiz.questions.length).fill(''));
    } catch (error) {
      console.error('Quiz başlatılamadı:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (answer) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = answer;
    setAnswers(newAnswers);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`/quizzes/attempt/${attempt}/complete`, {
        answers: answers.map((answer, index) => ({
          questionId: quiz.questions[index]._id,
          answer
        })),
        timeSpent: quiz.timeLimit * 60 - timeLeft
      });
      navigate(`/student/quiz/${quizId}/result`, { state: response.data });
    } catch (error) {
      console.error('Quiz gönderilemedi:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Quiz yükleniyor...</div>;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="take-quiz">
      <div className="quiz-header">
        <h1>{quiz?.title}</h1>
        <div className="quiz-timer">
          <span className="timer-icon">⏱️</span>
          <span className={`timer-value ${timeLeft < 300 ? 'warning' : ''}`}>
            {formatTime(timeLeft)}
          </span>
        </div>
      </div>

      <div className="quiz-progress">
        Soru {currentQuestion + 1} / {quiz?.questions.length}
      </div>

      <div className="question-container">
        <h2>{quiz?.questions[currentQuestion].question}</h2>

        {quiz?.questions[currentQuestion].type === 'multiple' ? (
          <div className="options-grid">
            {quiz?.questions[currentQuestion].options.map((option, index) => (
              <label key={index} className={`option-item ${answers[currentQuestion] === option ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="question"
                  value={option}
                  checked={answers[currentQuestion] === option}
                  onChange={() => handleAnswer(option)}
                />
                <span className="option-text">{option}</span>
              </label>
            ))}
          </div>
        ) : (
          <div className="truefalse-options">
            <label className={`option-item ${answers[currentQuestion] === 'true' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="question"
                value="true"
                checked={answers[currentQuestion] === 'true'}
                onChange={() => handleAnswer('true')}
              />
              <span className="option-text">Doğru</span>
            </label>
            <label className={`option-item ${answers[currentQuestion] === 'false' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="question"
                value="false"
                checked={answers[currentQuestion] === 'false'}
                onChange={() => handleAnswer('false')}
              />
              <span className="option-text">Yanlış</span>
            </label>
          </div>
        )}
      </div>

      <div className="quiz-navigation">
        <button
          className="btn-nav"
          onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
          disabled={currentQuestion === 0}
        >
          ← Önceki
        </button>
        
        <div className="question-dots">
          {quiz?.questions.map((_, index) => (
            <span
              key={index}
              className={`dot ${index === currentQuestion ? 'active' : ''} ${answers[index] ? 'answered' : ''}`}
              onClick={() => setCurrentQuestion(index)}
            />
          ))}
        </div>

        {currentQuestion === quiz?.questions.length - 1 ? (
          <button
            className="btn-submit"
            onClick={handleSubmit}
            disabled={submitting || answers.includes('')}
          >
            {submitting ? 'Gönderiliyor...' : 'Quiz\'i Bitir'}
          </button>
        ) : (
          <button
            className="btn-nav"
            onClick={() => setCurrentQuestion(prev => Math.min(quiz?.questions.length - 1, prev + 1))}
          >
            Sonraki →
          </button>
        )}
      </div>
    </div>
  );
}

export default TakeQuiz;